import { useAuftragRepository } from '@Hooks/useRepository';
import { type ApiContainerAction } from '@Model';
import { ContainerActionContainerTypeRepository } from '@Repositories/Auftragsbearbeitung';
import { isOk, type Result, useToasts } from '@synqo/frontend-component-library';
import { useCallback, useState } from 'react';

export interface ContainerTypeChange {
    changeType: (actionIds: readonly string[], containerTypeId: string) => Promise<boolean>;
    clearType: (actionIds: readonly string[], containerTypeId: string) => Promise<boolean>;
    isChanging: boolean;
}

/** Changes the container type of several rows, one after the other, stopping at the first refusal. */
// @ai-generated 2026-10-15T07:18:24+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function useContainerTypeChange(
    orderId: string,
    mergeAction: (action: ApiContainerAction) => void,
): ContainerTypeChange {
    const types = useAuftragRepository(ContainerActionContainerTypeRepository);
    const { pushAllToasts } = useToasts();
    const [isChanging, setIsChanging] = useState(false);

    const eachRow = useCallback(
        async (actionIds: readonly string[], write: (actionId: string) => Promise<Result<ApiContainerAction>>) => {
            setIsChanging(true);
            try {
                for (const actionId of actionIds) {
                    const result = await write(actionId);
                    pushAllToasts(result.messages);
                    if (!isOk(result)) {
                        return false;
                    }

                    mergeAction(result.data);
                }

                return true;
            } finally {
                setIsChanging(false);
            }
        },
        [mergeAction, pushAllToasts],
    );

    const changeType = useCallback(
        (actionIds: readonly string[], containerTypeId: string) =>
            eachRow(actionIds, (actionId) => types.addLinkedObject(containerTypeId, actionId, orderId)),
        [eachRow, types, orderId],
    );

    const clearType = useCallback(
        (actionIds: readonly string[], containerTypeId: string) =>
            eachRow(actionIds, (actionId) => types.deleteLinkedObject(containerTypeId, actionId, orderId)),
        [eachRow, types, orderId],
    );

    return { changeType, clearType, isChanging };
}
